import { useState } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import { useTeam } from '@/hooks/useTeam'
import { useTeamUsers } from '@/hooks/useTeamUsers'
import { useInviteCode, usePendingMembers, useMembershipAction, useRemoveMember } from '@/hooks/useUserManagement'
import { useUpdateTeam, useGrantAdmin, useRevokeAdmin } from '@/hooks/useTeamManagement'
import { apiClient } from '@/api/client'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'
import { 
  Users, 
  UserPlus, 
  Clock, 
  CheckCircle, 
  XCircle, 
  Trash2, 
  Copy,
  Settings,
  Crown,
  Edit2,

  Upload
} from 'lucide-react'

export function AdminDashboard() {
  const { getAccessTokenSilently } = useAuth0()
  // @ts-ignore - useTeam may not have refetch
  const { currentTeam, refetch: refetchTeam } = useTeam()
  const { data: users, isLoading: usersLoading } = useTeamUsers(currentTeam?.id)
  const { data: pendingMembers, isLoading: pendingLoading } = usePendingMembers(currentTeam?.id)
  
  const [showInviteForm, setShowInviteForm] = useState(false)
  const [inviteCode, setInviteCode] = useState('')
  const [expiresInDays, setExpiresInDays] = useState(7)
  const [maxUses, setMaxUses] = useState(1)

  const createInviteCode = useInviteCode(currentTeam?.id)
  const approveMembership = useMembershipAction()
  const removeMember = useRemoveMember()
  const updateTeam = useUpdateTeam(currentTeam?.id)
  const grantAdmin = useGrantAdmin(currentTeam?.id)
  const revokeAdmin = useRevokeAdmin(currentTeam?.id)
  
  const [showTeamEdit, setShowTeamEdit] = useState(false)
  const [teamName, setTeamName] = useState('')
  const [teamDescription, setTeamDescription] = useState('')
  const [teamPictureUrl, setTeamPictureUrl] = useState('')
  const [uploadingTeamPicture, setUploadingTeamPicture] = useState(false)

  const handleCreateInvite = async () => {
    try {
      const result = await createInviteCode.mutateAsync({
        expires_in_days: expiresInDays,
        max_uses: maxUses
      })
      setInviteCode(result.code)
      setShowInviteForm(false)
    } catch (error) {
      console.error('Failed to create invite code:', error)
    }
  }

  const handleApproveMember = async (membershipId: string) => {
    try {
      await approveMembership.mutateAsync({ membershipId, action: 'approve' })
    } catch (error) {
      console.error('Failed to approve member:', error)
    }
  }

  const handleRejectMember = async (membershipId: string) => {
    try {
      await approveMembership.mutateAsync({ membershipId, action: 'reject' })
    } catch (error) {
      console.error('Failed to reject member:', error)
    }
  }

  const handleRemoveMember = async (membershipId: string) => {
    if (window.confirm('Are you sure you want to remove this member?')) {
      try {
        await removeMember.mutateAsync(membershipId)
      } catch (error) {
        console.error('Failed to remove member:', error)
      }
    }
  }

  const copyInviteCode = () => {
    navigator.clipboard.writeText(inviteCode)
  }

  const handleEditTeam = () => {
    setTeamName(currentTeam?.name || '')
    setTeamDescription(currentTeam?.description || '')
    setTeamPictureUrl(currentTeam?.picture_url || '')
    setShowTeamEdit(true)
  }

  const handleTeamPictureUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    setUploadingTeamPicture(true)

    try {
      const token = await getAccessTokenSilently({
        authorizationParams: {
          scope: 'openid profile email',
        },
      })

      const formData = new FormData()
      formData.append('image', file)

      apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`
      const response = await apiClient.post('/upload/team-picture', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })

      // Use current origin in production, localhost in development
      const isProduction = window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1'
      const baseUrl = isProduction ? window.location.origin : (import.meta.env.VITE_API_URL || 'http://localhost:3002')
      const uploadedUrl = `${baseUrl}${response.data.url}`
      setTeamPictureUrl(uploadedUrl)
      
      await updateTeam.mutateAsync({ 
        name: teamName,
        description: teamDescription,
        picture_url: uploadedUrl 
      })
      
      refetchTeam()
    } catch (error) {
      console.error('Failed to upload team picture:', error)
    } finally {
      setUploadingTeamPicture(false)
    }
  }

  const handleSaveTeam = async () => {
    try {
      await updateTeam.mutateAsync({ 
        name: teamName,
        description: teamDescription,
        picture_url: teamPictureUrl 
      })
      setShowTeamEdit(false)
      refetchTeam()
    } catch (error) {
      console.error('Failed to update team:', error)
    }
  }

  const handleDeleteTeamPicture = async () => {
    if (!window.confirm('Are you sure you want to delete the team picture?')) {
      return
    }

    try {
      await updateTeam.mutateAsync({ 
        name: teamName,
        description: teamDescription,
        picture_url: null as any
      })
      setTeamPictureUrl('')
      refetchTeam()
    } catch (error) {
      console.error('Failed to delete team picture:', error)
    }
  }

  const handleGrantAdmin = async (userId: string) => {
    if (window.confirm('Are you sure you want to grant admin privileges to this user?')) {
      try {
        await grantAdmin.mutateAsync(userId)
      } catch (error) {
        console.error('Failed to grant admin:', error)
      }
    }
  }

  const handleRevokeAdmin = async (userId: string) => {
    if (window.confirm('Are you sure you want to revoke admin privileges from this user?')) {
      try {
        await revokeAdmin.mutateAsync(userId)
      } catch (error) {
        console.error('Failed to revoke admin:', error)
      }
    }
  }

  if (usersLoading || pendingLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  const approvedUsers = users?.filter((u: any) => u.status === 'approved') || []
  const pendingUsers = pendingMembers || []

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Admin Dashboard</h2>
          <p className="text-gray-600">Manage {currentTeam?.name} team</p>
        </div>
        <div className="flex items-center space-x-2">
          <Crown className="h-5 w-5 text-yellow-500" />
          <span className="text-sm font-medium text-gray-700">Admin</span>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center">
            <Users className="h-8 w-8 text-blue-500" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">Total Members</p>
              <p className="text-2xl font-bold text-gray-900">{approvedUsers.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center">
            <Clock className="h-8 w-8 text-yellow-500" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">Pending Requests</p>
              <p className="text-2xl font-bold text-gray-900">{pendingUsers.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center">
            <Settings className="h-8 w-8 text-green-500" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">Team</p>
              <p className="text-lg font-bold text-gray-900">{currentTeam?.name}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Team Settings */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900 flex items-center">
            <Settings className="h-5 w-5 mr-2" />
            Team Settings
          </h3>
          {!showTeamEdit && (
            <button
              onClick={handleEditTeam}
              className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm font-medium rounded text-gray-700 bg-white hover:bg-gray-50"
            >
              <Edit2 className="h-4 w-4 mr-1" />
              Edit Team
            </button>
          )}
        </div>

        {showTeamEdit ? (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Team Name</label>
              <input
                type="text"
                value={teamName}
                onChange={(e) => setTeamName(e.target.value)}
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                placeholder="Enter team name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description (Optional)</label>
              <textarea
                value={teamDescription}
                onChange={(e) => setTeamDescription(e.target.value)}
                rows={3}
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                placeholder="Enter team description"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Team Picture</label>
              <div className="mb-3">
                <label
                  htmlFor="team-picture-upload"
                  className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 ${
                    uploadingTeamPicture
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-green-600 hover:bg-green-700 cursor-pointer'
                  }`}
                >
                  {uploadingTeamPicture ? (
                    <>
                      <LoadingSpinner size="sm" className="mr-2" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="h-4 w-4 mr-2" />
                      Upload Picture
                    </>
                  )}
                </label>
                <input
                  id="team-picture-upload"
                  type="file"
                  accept="image/*"
                  onChange={handleTeamPictureUpload}
                  disabled={uploadingTeamPicture}
                  className="hidden"
                />
                <span className="ml-3 text-xs text-gray-500">
                  JPG, PNG, GIF, or WEBP (max 5MB)
                </span>
              </div>
              
              <div className="relative my-3">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300"></div>
                </div>
                <div className="relative flex justify-center text-xs">
                  <span className="px-2 bg-white text-gray-500">or paste URL</span>
                </div>
              </div>
              
              <input
                type="url"
                value={teamPictureUrl}
                onChange={(e) => setTeamPictureUrl(e.target.value)}
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                placeholder="https://example.com/team-logo.png"
                disabled={uploadingTeamPicture}
              />
              <p className="mt-1 text-xs text-gray-500">
                Upload a file or paste a direct link to your team logo
              </p>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={handleSaveTeam}
                disabled={updateTeam.isPending || uploadingTeamPicture}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
              >
                {updateTeam.isPending ? 'Saving...' : 'Save Changes'}
              </button>
              <button
                onClick={() => setShowTeamEdit(false)}
                className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {currentTeam?.picture_url && (
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <img 
                    src={currentTeam.picture_url} 
                    alt={currentTeam.name} 
                    className="h-16 w-16 rounded-lg object-cover border-2 border-gray-200"
                  />
                  <p className="text-sm text-gray-500">Team Picture</p>
                </div>
                <button
                  onClick={handleDeleteTeamPicture}
                  disabled={updateTeam.isPending}
                  className="inline-flex items-center px-3 py-2 border border-red-300 text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50 disabled:opacity-50"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete Picture
                </button>
              </div>
            )}
            <div>
              <p className="text-sm font-medium text-gray-500">Team Name</p>
              <p className="text-gray-900">{currentTeam?.name}</p>
            </div>
            {currentTeam?.description && (
              <div>
                <p className="text-sm font-medium text-gray-500">Description</p>
                <p className="text-gray-900">{currentTeam.description}</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Invite Code Generation */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
          <UserPlus className="h-5 w-5 mr-2" />
          Invite New Members
        </h3>

        {!showInviteForm && !inviteCode && (
          <button
            onClick={() => setShowInviteForm(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
          >
            <UserPlus className="h-4 w-4 mr-2" />
            Generate Invite Code
          </button>
        )}

        {showInviteForm && !inviteCode && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Expires in (days)
                </label>
                <input
                  type="number"
                  min="1"
                  max="365"
                  value={expiresInDays}
                  onChange={(e) => setExpiresInDays(parseInt(e.target.value))}
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Max Uses
                </label>
                <input
                  type="number"
                  min="1"
                  max="1000"
                  value={maxUses}
                  onChange={(e) => setMaxUses(parseInt(e.target.value))}
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
              </div>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={handleCreateInvite}
                disabled={createInviteCode.isPending}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
              >
                {createInviteCode.isPending ? 'Generating...' : 'Generate Code'}
              </button>
              <button
                onClick={() => setShowInviteForm(false)}
                className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {inviteCode && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <h4 className="text-sm font-medium text-green-900 mb-2">Invite Code Generated!</h4>
            <div className="flex items-center space-x-2">
              <code className="flex-1 bg-white border border-green-300 rounded px-3 py-2 text-lg font-mono text-gray-900">
                {inviteCode}
              </code>
              <button
                onClick={copyInviteCode}
                className="px-3 py-2 border border-green-300 rounded-md text-sm font-medium text-green-700 bg-white hover:bg-green-50 flex items-center"
              >
                <Copy className="h-4 w-4 mr-1" />
                Copy
              </button>
            </div>
            <p className="text-sm text-green-700 mt-2">
              Share this code with new members. It expires in {expiresInDays} days and can be used {maxUses} times.
            </p>
            <button
              onClick={() => {
                setInviteCode('')
                setShowInviteForm(false)
              }}
              className="mt-3 text-sm text-green-600 hover:text-green-800"
            >
              Generate Another Code
            </button>
          </div>
        )}
      </div>

      {/* Pending Members */}
      {pendingUsers.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
            <Clock className="h-5 w-5 mr-2" />
            Pending Member Requests ({pendingUsers.length})
          </h3>
          <div className="space-y-3">
            {pendingUsers.map((member: any) => (
              <div key={member.membership_id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                <div>
                  <p className="font-medium text-gray-900">{member.display_name}</p>
                  <p className="text-sm text-gray-500">{member.email}</p>
                  <p className="text-xs text-gray-400">
                    Requested {new Date(member.joined_at).toLocaleDateString()}
                  </p>
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => handleApproveMember(member.membership_id)}
                    disabled={approveMembership.isPending}
                    className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 disabled:opacity-50"
                  >
                    <CheckCircle className="h-4 w-4 mr-1" />
                    Approve
                  </button>
                  <button
                    onClick={() => handleRejectMember(member.membership_id)}
                    disabled={approveMembership.isPending}
                    className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 disabled:opacity-50"
                  >
                    <XCircle className="h-4 w-4 mr-1" />
                    Reject
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Current Members */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
          <Users className="h-5 w-5 mr-2" />
          Current Members ({approvedUsers.length})
        </h3>
        <div className="space-y-3">
          {approvedUsers.map((member: any) => (
            <div key={member.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
              <div className="flex items-center space-x-3">
                <div>
                  <p className="font-medium text-gray-900 flex items-center">
                    {member.display_name}
                    {member.role === 'admin' && (
                      <Crown className="h-4 w-4 ml-2 text-yellow-500" />
                    )}
                  </p>
                  <p className="text-sm text-gray-500">{member.email}</p>
                  <p className="text-xs text-gray-400">
                    Joined {new Date(member.joined_at).toLocaleDateString()}
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                  member.role === 'admin' 
                    ? 'bg-yellow-100 text-yellow-800' 
                    : 'bg-blue-100 text-blue-800'
                }`}>
                  {member.role}
                </span>
                
                {/* Grant/Revoke Admin */}
                {member.role === 'admin' ? (
                  <button
                    onClick={() => handleRevokeAdmin(member.id)}
                    disabled={revokeAdmin.isPending}
                    className="px-2 py-1 text-xs text-yellow-700 bg-yellow-50 hover:bg-yellow-100 rounded border border-yellow-200 disabled:opacity-50"
                    title="Revoke admin"
                  >
                    Revoke Admin
                  </button>
                ) : (
                  <button
                    onClick={() => handleGrantAdmin(member.id)}
                    disabled={grantAdmin.isPending}
                    className="px-2 py-1 text-xs text-blue-700 bg-blue-50 hover:bg-blue-100 rounded border border-blue-200 disabled:opacity-50"
                    title="Grant admin"
                  >
                    Make Admin
                  </button>
                )}
                
                {/* Remove Member (only for non-admins) */}
                {member.role !== 'admin' && (
                  <button
                    onClick={() => handleRemoveMember(member.id)}
                    disabled={removeMember.isPending}
                    className="text-red-600 hover:text-red-800 disabled:opacity-50"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
